/**
 * Locker signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_summer_school`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lockerSignsPack: ToilePack = {
    slug: 'locker-signs',
    category: 'season',
    name: 'Locker signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'name', label: 'Name tag', subject: 'a landscape locker tag with a large clear empty panel in the middle for a name, "Locker" lettered small above it with a blank number line, and pencils along the edges'},
        {key: 'team', label: 'Team sign', subject: 'a locker sign lettered "Go Team!" across the top over a large blank name panel, a blank line for a jersey number and a pennant in the corner'},
        {key: 'cubby', label: 'Cubby label', subject: 'a print sheet of six small cubby labels in a 3x2 grid with cut lines, each label a blank name panel with a different small motif, an apple, a star, a rocket, a fish, a sun, a leaf'},
    ],
    styles: [
        {key: 'varsity', label: 'Varsity', style: 'varsity sports lettering, block capitals with a drop shadow, pennant and star shapes', palette: 'Navy blue, school red and white on cream card', framing: 'Laid out on a landscape page with an even margin, the name panel left clear at the centre'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn doodle tag, wobbly rounded lettering, small stars and pencils', palette: 'Bright blue, orange and yellow marker on white paper', framing: 'Laid out on a landscape page with a wide margin and a scalloped border'},
        {key: 'minimal', label: 'Minimal', style: 'clean modern label, one thin rule, light sans-serif lettering, generous empty space', palette: 'Charcoal with one sage-green accent on white', framing: 'Laid out on a landscape page with a broad quiet margin'},
    ],
    directions: ['sports team', 'rainbow', 'glitter'],
    directionHint: 'try: sports team',
    seo: {
        title: 'Locker signs: name tags, team sign, cubby labels',
        metaDescription: "Nine printable locker signs: name tags for a student's locker, a team sign and cubby labels, each with a blank name panel.",
        h1: 'Locker signs: name tags, team sign, cubby labels',
        tagline: 'Nine signs in three forms and three looks, the words spelled exactly and a clear panel for the name.',
        intro: "Name tags a teacher prints for each student's locker or cubby, or a team tapes to its lockers before a game. Each sign carries its words spelled exactly and leaves a clear blank panel for the name. Choose varsity, doodle or minimal. Type a different theme, such as sports team, rainbow or glitter, and the three styles follow the request.",
        useCases: [
            'a name tag for a school locker',
            'a team sign on lockers before a game',
            'cubby labels for a classroom',
            'a name label for a gym bag',
        ],
        faq: [
            {q: 'Where does the name go?', a: 'In the clear blank panel in the middle of each sign, or the panel on each cubby label.'},
            {q: 'What is on the cubby label sheet?', a: 'Six small labels in a grid, each with a blank name panel and a different motif: an apple, a star, a rocket, a fish, a sun and a leaf.'},
            {q: 'What does the team sign say?', a: 'Go Team across the top, over a blank name panel and a blank line for a jersey number.'},
            {q: 'Can I ask for a different theme?', a: 'Yes. Type a theme as a direction; the design follows the request and is not a guaranteed result.'},
        ],
    },
}
